import { parseRouteIdsFromPathname } from "@/utils/hooks/url/parse-route-ids";
import { useRouter } from "next/router";
import React, { PropsWithChildren, createContext, useMemo, useSyncExternalStore } from "react";

export interface RouteInfo {
  isReady: boolean;
  projectId?: string;
  environmentId?: string;
  runId?: string;
  featureFqn?: string;
  resolverFqnOrShortname?: string;
  deploymentId?: string;
  incidentId?: string;
  migrationId?: string;
  integrationId?: string;
  metaQueryId?: string;
  queryName?: string;
  errorId?: string;
  datasetId?: string;
  exportId?: string;
  branchName?: string;
  queryRunId?: string;
  queryPlanId?: string;
  traceId?: string;
  /** An agent session in the AI traces explorer, keyed by its OpenTelemetry session id. */
  traceSessionId?: string;
  offlineQueryId?: string;
  scriptTaskId?: string;
  datasetRevisionId?: string;
  dashboardId?: string;
  cronQueryId?: string;
  cronQueryRunId?: string;
  aggregateBackfillId?: string;
  cronQueryName?: string;
  cronAggBackfillName?: string;
  agentId?: string;
  jobId?: string;
  workflowId?: string;
  modelId?: string;
  modelVersion?: string;
  modelArtifactId?: string;
  modelDeploymentId?: string;
  serviceAccountName?: string;
  kubeServiceId?: string;
  podName?: string;
  nodeName?: string;
  autoscalerName?: string;
  kubeRouteId?: string;
  deploymentUid?: string;
  deploymentName?: string;
  jobName?: string;
  statefulSetName?: string;
  statefulSetUid?: string;
  persistentVolumeName?: string;
  jobUid?: string;
  vpcId?: string;
  clusterId?: string;
  registryId?: string;
  storageId?: string;
  credentialId?: string;
  resourceGroupName?: string;
  jobQueueConsumerId?: string;
  queryId?: string;
  metadataScheduledJobId?: string;
  scalingGroupIdOrName?: string;
  scalingGroupRevisionId?: string;
  deploymentRevisionId?: string;
  containerId?: string;
  connectionId?: string;
  // Distinct from `connectionId` (a cloud integration) — a Model Gateway provider
  // connection lives in a different service with its own id namespace.
  routerConnectionId?: string;
  routerRouteId?: string;
  routerKeyHash?: string;
  routerUsagePoolId?: string;
  sandboxId?: string;
  notebookId?: string;
  volumeName?: string;
  workflowName?: string;
  contentHash?: string;
  dataframeRunId?: string;
  containerSnapshotId?: string;
  externalFunctionName?: string;
  chartId?: string;
  monitorId?: string;
  serverName?: string;
  conversationId?: string;
  templateId?: string;
  policyName?: string;
}

export const RouteInfoContext = createContext<RouteInfo | undefined>(undefined);

// The pathname never changes without a render (Next re-renders on route
// transitions), so the store never needs to notify subscribers itself.
const subscribeToNothing = () => () => {};
const EMPTY_PATHNAME_ROUTE_IDS: ReturnType<typeof parseRouteIdsFromPathname> = {};

/**
 * Project/environment ids parsed straight from `window.location.pathname`,
 * used only while `router.isReady` is false. Statically-rendered dashboard
 * pages hydrate with an empty `router.query`, so without this fallback every
 * id-gated page and `enabled: environmentId != null` query waits for Next's
 * post-hydration router update even though the ids are literally in the URL.
 *
 * useSyncExternalStore keeps the fallback hydration-safe: the server snapshot
 * is null, so the SSR/SSG markup and the hydration render agree (ids
 * undefined), and React re-renders with the real pathname immediately after
 * hydration — without waiting for the router.
 */
const usePathnameRouteIdsFallback = (isReady: boolean) => {
  const pathname = useSyncExternalStore(
    subscribeToNothing,
    () => window.location.pathname,
    () => null,
  );
  return useMemo(
    () =>
      isReady || pathname == null ? EMPTY_PATHNAME_ROUTE_IDS : parseRouteIdsFromPathname(pathname),
    [isReady, pathname],
  );
};

export const useComputedRouteInfo = (): RouteInfo => {
  // Next decodes dynamic segments and query values once; decoding again corrupts literal %2F IDs.
  const router = useRouter();
  const isReady = router.isReady;
  const pathnameFallback = usePathnameRouteIdsFallback(isReady);
  const projectId = (router.query.projectId as string | undefined) ?? pathnameFallback.projectId;
  const environmentId =
    (router.query.environmentId as string | undefined) ?? pathnameFallback.environmentId;
  const runId = router.query.runId as string | undefined;
  const featureFqn = router.query.featureFqn as string | undefined;
  const resolverFqnOrShortname = router.query.resolverFqn as string | undefined;
  const deploymentId = router.query.deploymentId as string | undefined;
  const incidentId = router.query.incidentId as string | undefined;
  const migrationId = router.query.migrationId as string | undefined;
  const integrationId = router.query.integrationId as string | undefined;
  const metaQueryId = router.query.metaQueryId as string | undefined;
  const queryName = router.query.queryName as string | undefined;
  const errorId = router.query.errorId as string | undefined;
  const datasetId = router.query.datasetId as string | undefined;
  const exportId = router.query.exportId as string | undefined;
  const branchName = router.query.branchName as string | undefined;
  const queryRunId = router.query.queryRunId as string | undefined;
  const queryPlanId = router.query.queryPlanId as string | undefined;
  const traceId = router.query.traceId as string | undefined;
  const traceSessionId = router.query.traceSessionId as string | undefined;
  const offlineQueryId = router.query.offlineQueryId as string | undefined;
  const scriptTaskId = router.query.scriptTaskId as string | undefined;
  const datasetRevisionId = router.query.datasetRevisionId as string | undefined;
  const dashboardId = router.query.dashboardId as string | undefined;
  const cronQueryId = router.query.cronQueryId as string | undefined;
  const cronQueryRunId = router.query.cronQueryRunId as string | undefined;
  const aggregateBackfillId = router.query.aggregateBackfillId as string | undefined;
  const cronQueryName = router.query.cronQueryName as string | undefined;
  const cronAggBackfillName = router.query.cronAggBackfillName as string | undefined;
  const agentId = router.query.agentId as string | undefined;
  const jobId = router.query.jobId as string | undefined;
  const workflowId = router.query.workflowId as string | undefined;
  const modelId = router.query.modelId as string | undefined;
  const modelVersion = router.query.modelVersion as string | undefined;
  const modelArtifactId = router.query.modelArtifactId as string | undefined;
  const modelDeploymentId = router.query.modelDeploymentId as string | undefined;
  const serviceAccountName = router.query.serviceAccountName as string | undefined;
  const kubeServiceId = router.query.kubeServiceId as string | undefined;
  const podName = router.query.podName as string | undefined;
  const nodeName = router.query.nodeName as string | undefined;
  const autoscalerName = router.query.autoscalerName as string | undefined;
  const kubeRouteId = router.query.kubeRouteId as string | undefined;
  const deploymentUid = router.query.deploymentUid as string | undefined;
  const deploymentName = router.query.deploymentName as string | undefined;
  const jobName = router.query.jobName as string | undefined;
  const statefulSetName = router.query.statefulSetName as string | undefined;
  const statefulSetUid = router.query.statefulSetUid as string | undefined;
  const persistentVolumeName = router.query.persistentVolumeName as string | undefined;
  const jobUid = router.query.jobUid as string | undefined;
  const vpcId = router.query.vpcId as string | undefined;
  const clusterId = router.query.clusterId as string | undefined;
  const registryId = router.query.registryId as string | undefined;
  const storageId = router.query.storageId as string | undefined;
  const credentialId = router.query.credentialId as string | undefined;
  const resourceGroupName = router.query.resourceGroupName as string | undefined;
  const jobQueueConsumerId = router.query.jobQueueConsumerId as string | undefined;
  const queryId = router.query.queryId as string | undefined;
  const metadataScheduledJobId = router.query.metadataScheduledJobId as string | undefined;
  const scalingGroupIdOrName = router.query.scalingGroupIdOrName as string | undefined;
  const scalingGroupRevisionId = router.query.scalingGroupRevisionId as string | undefined;
  const deploymentRevisionId = router.query.deploymentRevisionId as string | undefined;
  const containerId = router.query.containerId as string | undefined;
  const connectionId = router.query.connectionId as string | undefined;
  const routerConnectionId = router.query.routerConnectionId as string | undefined;
  const routerRouteId = router.query.routerRouteId as string | undefined;
  const routerKeyHash = router.query.routerKeyHash as string | undefined;
  const routerUsagePoolId = router.query.routerUsagePoolId as string | undefined;
  const sandboxId = router.query.sandboxId as string | undefined;
  const notebookId = router.query.notebookId as string | undefined;
  const volumeName = router.query.volumeName as string | undefined;
  const workflowName = router.query.workflowName as string | undefined;
  const contentHash = router.query.contentHash as string | undefined;
  const dataframeRunId = router.query.dataframeRunId as string | undefined;
  const containerSnapshotId = router.query.containerSnapshotId as string | undefined;
  const externalFunctionName = router.query.externalFunctionName as string | undefined;
  const chartId = router.query.chartId as string | undefined;
  const monitorId = router.query.monitorId as string | undefined;
  const serverName = router.query.serverName as string | undefined;
  const conversationId = router.query.conversationId as string | undefined;
  const templateId = router.query.templateId as string | undefined;
  const policyName = router.query.policyName as string | undefined;

  return useMemo(() => {
    return {
      isReady,
      projectId,
      environmentId,
      runId,
      featureFqn,
      resolverFqnOrShortname,
      deploymentId,
      incidentId,
      migrationId,
      integrationId,
      metaQueryId,
      queryName,
      errorId,
      datasetId,
      exportId,
      branchName,
      queryRunId,
      queryPlanId,
      traceId,
      traceSessionId,
      offlineQueryId,
      scriptTaskId,
      datasetRevisionId,
      dashboardId,
      cronQueryId,
      cronQueryRunId,
      aggregateBackfillId,
      cronQueryName,
      cronAggBackfillName,
      agentId,
      jobId,
      workflowId,
      modelId,
      modelVersion,
      modelArtifactId,
      modelDeploymentId,
      serviceAccountName,
      kubeServiceId,
      podName,
      nodeName,
      autoscalerName,
      kubeRouteId,
      deploymentUid,
      deploymentName,
      jobName,
      statefulSetName,
      statefulSetUid,
      persistentVolumeName,
      jobUid,
      vpcId,
      clusterId,
      registryId,
      storageId,
      credentialId,
      resourceGroupName,
      jobQueueConsumerId,
      queryId,
      metadataScheduledJobId,
      scalingGroupIdOrName,
      scalingGroupRevisionId,
      deploymentRevisionId,
      containerId,
      connectionId,
      routerConnectionId,
      routerRouteId,
      routerKeyHash,
      routerUsagePoolId,
      sandboxId,
      notebookId,
      volumeName,
      workflowName,
      contentHash,
      dataframeRunId,
      containerSnapshotId,
      externalFunctionName,
      chartId,
      monitorId,
      serverName,
      conversationId,
      templateId,
      policyName,
    };
  }, [
    isReady,
    projectId,
    environmentId,
    runId,
    featureFqn,
    resolverFqnOrShortname,
    deploymentId,
    incidentId,
    migrationId,
    integrationId,
    metaQueryId,
    queryName,
    errorId,
    datasetId,
    exportId,
    branchName,
    queryRunId,
    queryPlanId,
    traceId,
    traceSessionId,
    offlineQueryId,
    scriptTaskId,
    datasetRevisionId,
    dashboardId,
    cronQueryId,
    cronQueryRunId,
    aggregateBackfillId,
    cronQueryName,
    cronAggBackfillName,
    agentId,
    jobId,
    workflowId,
    modelId,
    modelVersion,
    modelArtifactId,
    modelDeploymentId,
    serviceAccountName,
    kubeServiceId,
    podName,
    nodeName,
    autoscalerName,
    kubeRouteId,
    deploymentUid,
    deploymentName,
    jobName,
    statefulSetName,
    statefulSetUid,
    persistentVolumeName,
    jobUid,
    vpcId,
    clusterId,
    registryId,
    storageId,
    credentialId,
    resourceGroupName,
    jobQueueConsumerId,
    queryId,
    metadataScheduledJobId,
    scalingGroupIdOrName,
    scalingGroupRevisionId,
    deploymentRevisionId,
    containerId,
    connectionId,
    routerConnectionId,
    routerRouteId,
    routerKeyHash,
    routerUsagePoolId,
    sandboxId,
    notebookId,
    volumeName,
    workflowName,
    contentHash,
    dataframeRunId,
    containerSnapshotId,
    externalFunctionName,
    chartId,
    monitorId,
    serverName,
    conversationId,
    templateId,
    policyName,
  ]);
};

export const RouteInfoProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const routeInfo = useComputedRouteInfo();
  return <RouteInfoContext.Provider value={routeInfo}>{children}</RouteInfoContext.Provider>;
};
